/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
@file:Suppress("MatchingDeclarationName")

package cmp.navigation.authenticated

import androidx.navigation.NavController
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavOptions
import androidx.navigation.navigation
import cmp.navigation.authenticatednavbar.AuthenticatedNavbarRoute
import cmp.navigation.authenticatednavbar.authenticatedNavbarGraph
import kotlinx.serialization.Serializable
import studio.oddpods.cauce.feature.reminders.navigateToReminders
import studio.oddpods.cauce.feature.reminders.remindersDestination
import studio.oddpods.cauce.feature.sessions.createRecurringScheduleDestination
import studio.oddpods.cauce.feature.sessions.editRecurringScheduleDestination
import studio.oddpods.cauce.feature.sessions.editSessionDestination
import studio.oddpods.cauce.feature.sessions.logSessionDestination
import studio.oddpods.cauce.feature.sessions.navigateToCreateRecurringSchedule
import studio.oddpods.cauce.feature.sessions.navigateToEditRecurringSchedule
import studio.oddpods.cauce.feature.sessions.navigateToEditSession
import studio.oddpods.cauce.feature.sessions.navigateToLogSession
import studio.oddpods.cauce.feature.sessions.navigateToSessionDetail
import studio.oddpods.cauce.feature.sessions.sessionDetailDestination
import studio.oddpods.cauce.feature.settings.navigateToNotification
import studio.oddpods.cauce.feature.settings.navigateToReminderSettings
import studio.oddpods.cauce.feature.settings.notificationDestination
import studio.oddpods.cauce.feature.settings.reminderSettingsDestination

@Serializable
internal data object AuthenticatedGraphRoute

internal fun NavController.navigateToAuthenticatedGraph(navOptions: NavOptions? = null) {
    navigate(route = AuthenticatedGraphRoute, navOptions = navOptions)
}

internal fun NavGraphBuilder.authenticatedGraph(
    navController: NavController,
) {
    navigation<AuthenticatedGraphRoute>(
        startDestination = AuthenticatedNavbarRoute,
    ) {
        authenticatedNavbarGraph(
            onLogSession = { patientId -> navController.navigateToLogSession(patientId) },
            onNavigateToReminders = { navController.navigateToReminderSettings() },
            onNavigateToNotifications = { navController.navigateToNotification() },
            onOpenReminders = { navController.navigateToReminders() },
            onSendReminder = { patientId -> navController.navigateToReminders(patientId = patientId) },
            onCreateRecurringSchedule = { patientId -> navController.navigateToCreateRecurringSchedule(patientId) },
            onEditRecurringSchedule = { scheduleId, patientName ->
                navController.navigateToEditRecurringSchedule(scheduleId, patientName)
            },
            onSessionClick = { sessionId -> navController.navigateToSessionDetail(sessionId) },
        )

        notificationDestination(
            onBackClick = navController::popBackStack,
        )

        reminderSettingsDestination(
            onBackClick = navController::popBackStack,
        )

        logSessionDestination(
            onBackClick = navController::popBackStack,
        )

        remindersDestination(
            onBackClick = navController::popBackStack,
        )

        createRecurringScheduleDestination(
            onBackClick = navController::popBackStack,
        )

        sessionDetailDestination(
            onBackClick = navController::popBackStack,
            onEditClick = { sessionId -> navController.navigateToEditSession(sessionId) },
        )

        editSessionDestination(
            onBackClick = navController::popBackStack,
        )

        editRecurringScheduleDestination(
            onBackClick = navController::popBackStack,
        )
    }
}
